import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment";
import AdminDiamondReportService from "../../services/reports-AdminDiamond-service";
import { Spin } from "antd";
import Chart from "react-google-charts";
import { chartColors } from "../reports/colors";

const AdminDiamondPriceComp = ({
  globalDiamonds,
  mainForDealer,
  isApplyFilter,
}) => {
  const { t } = useTranslation();

  const [dealerId, setDealerId] = useState();
  const loginDetials = useSelector((state) => state.loginReducer);
  const [LoadChartReportsLoading, setLoadChartReportsLoading] = useState(false);

  // New state for custom legends and max entries
  const [chartData, setChartData] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [IsReportEmpty, setIsReportEmpty] = useState(true);

  // Configuration
  const MAX_ENTRIES = 15; // Maximum slices to show

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      setDealerId(loginDetials.loginDetials.responseData.dealerId);
      handleLoadChartReports(
        loginDetials.loginDetials.responseData.dealerId,
        mainForDealer
      );
    }
  }, [isApplyFilter]);

  // Process chart data with max entries limit
  const processChartData = (priceList) => {
    if (!priceList?.length) {
      return { chartData: [], colors: [] };
    }

    // Filter out zero values and sort by total (descending)
    const filteredData = priceList.filter((item) => item.TOTAL > 0);
    const sortedData = [...filteredData].sort((a, b) => b.TOTAL - a.TOTAL);

    // Apply max entries limit
    const topData = sortedData.slice(0, MAX_ENTRIES);
    const remainingData = sortedData.slice(MAX_ENTRIES);

    // Calculate "Others" if needed
    const othersValue = remainingData.reduce(
      (sum, item) => sum + item.TOTAL,
      0
    );
    if (othersValue > 0 && remainingData.length > 0) {
      topData.push({
        Range: t("Others"),
        TOTAL: othersValue,
        isOthers: true,
      });
    }

    // Calculate total for percentages
    const total = topData.reduce((sum, item) => sum + item.TOTAL, 0);

    // Prepare chart data
    const processedChartData = [["Price Range", "Count"]];
    const colors = [];

    topData.forEach((item, index) => {
      const percentage =
        total > 0 ? ((item.TOTAL / total) * 100).toFixed(1) : 0;
      const color = item.isOthers
        ? "#CCCCCC"
        : chartColors[index % chartColors.length];
      colors.push(color);

      processedChartData.push([`${item.Range} (${percentage}%)`, item.TOTAL]);
    });

    return { chartData: processedChartData, colors };
  };

  const handleLoadChartReports = (dealerId, forDealer) => {
    let inputData = {
      dealerID: dealerId.toString(),
      forDealer: forDealer ? forDealer.toString() : "",
      fromDate: globalDiamonds.dataRangeFrom
        ? moment(globalDiamonds.dataRangeFrom).format("YYYY-MM-DD")
        : "2020-08-17",
      toDate: globalDiamonds.dataRangeTo
        ? moment(globalDiamonds.dataRangeTo).format("YYYY-MM-DD")
        : "2022-09-15",
      retailerIds: globalDiamonds.retailerIds.toString(),
      vendorIds: globalDiamonds.vendorIds.toString(),
      selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
      type: "Global",
      reportName: "ExtDiamondChrt",
    };

    setLoadChartReportsLoading(true);

    AdminDiamondReportService.LoadChartReports(inputData)
      .then((response) => {
        if (
          response.data.message === "Success" &&
          response.data.responseData?.DiamondPrice
        ) {
          const priceList = response.data.responseData.DiamondPrice;

          // Check if all data is zero
          const hasData = priceList.some((item) => item.TOTAL > 0);
          setIsReportEmpty(!hasData);

          // Process data with max entries limit
          const { chartData: processedData, colors } =
            processChartData(priceList);
          setChartData(processedData);
          setColorData(colors);
        } else {
          setChartData([]);
          setColorData([]);
          setIsReportEmpty(true);
        }
      })
      .catch((error) => {
        setChartData([]);
        setColorData([]);
        setIsReportEmpty(true);
      })
      .finally(() => setLoadChartReportsLoading(false));
  };

  const handleClick = ({ chartWrapper }) => {
    const chart = chartWrapper.getChart();
    const selection = chart.getSelection();
    if (selection.length > 0) {
      const selectedItem = selection[0];
      const label = chartData[selectedItem.row + 1][0];
      const result = label.split(" (")[0];
      // Handle click event if needed
      console.log("Selected Price Range:", result);
    }
  };

  return (
    <React.Fragment>
      <div className="col-lg-12 mt-4">
        <label>
          <b>{t("Diamond Price")}</b>
        </label>

        <Spin spinning={LoadChartReportsLoading} size="large">
          {!IsReportEmpty && chartData.length > 1 ? (
            <>
              {/* Custom Legend */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  marginBottom: "10px",
                }}
              >
                {chartData.slice(1).map((row, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "4px 8px",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: colorData[index],
                        marginRight: "6px",
                        borderRadius: "3px",
                      }}
                    ></div>
                    {row[0].replace(/\s*\([^)]*\)\s*/g, "")} ({row[1]})
                  </div>
                ))}
              </div>

              {/* Centered Pie Chart */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Chart
                  chartType="PieChart"
                  data={chartData}
                  width={window.innerWidth > 500 ? "800px" : "320px"}
                  height={window.innerWidth > 500 ? "500px" : "300px"}
                  options={{
                    is3D: true,
                    pieSliceText: "percentage",
                    tooltip: { trigger: "focus", text: "text" },
                    chartArea: {
                      left: "10%",
                      top: "5%",
                      width: "80%",
                      height: "90%",
                    },
                    colors: colorData,
                    legend: { position: "none" },
                  }}
                  chartEvents={[
                    {
                      eventName: "select",
                      callback: handleClick,
                    },
                  ]}
                />
              </div>
            </>
          ) : (
            <div className="no-report-render">
              <div className="desc-space">
                <h4 className="text-center">{t("No Data Found")}</h4>
              </div>
            </div>
          )}
        </Spin>
      </div>
    </React.Fragment>
  );
};

export default AdminDiamondPriceComp;
